/**
 * GenericHeroSection — a fully content-agnostic hero section.
 *
 * Props:
 *   eyebrow        string   — small label above heading
 *   heading        string   — main headline
 *   subheading     string   — secondary headline line
 *   body           string   — paragraph body text (multiline)
 *   alignment      'left'|'center'|'right'   default 'left'
 *   backgroundColor  string — CSS color for the section background
 *   backgroundImage  string — URL for a full-bleed background image
 *   buttons        Array<{ id, label, href }> — CTA buttons
 *
 * CRITICAL: empty props render nothing — no blank Typography nodes.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import RevealOnScroll from '../../components/RevealOnScroll';

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

const TEXT_ALIGN_MAP = {
  left: 'left',
  center: 'center',
  right: 'right',
};

export default function GenericHeroSection({
  eyebrow,
  heading,
  subheading,
  body,
  alignment,
  backgroundColor,
  backgroundImage,
  buttons,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const safeButtons = Array.isArray(buttons) ? buttons : [];
  const alignItems = ALIGN_MAP[alignment] || 'flex-start';
  const textAlign = TEXT_ALIGN_MAP[alignment] || 'left';

  // Build background styles
  const bgSx = {};
  if (backgroundImage) {
    // CSS background shorthand handles image + fallback colour in one property
    bgSx.background = `url(${backgroundImage}) center/cover no-repeat`;
  } else if (backgroundColor) {
    // Solid admin-set colour — bgcolor maps to background-color which is correct
    bgSx.bgcolor = backgroundColor;
  } else {
    // Gradient fallback: must use `background`, not `bgcolor`/background-color,
    // because CSS background-color cannot render a gradient value.
    bgSx.background = isDark
      ? 'rgba(27,54,93,0.18)'
      : `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`;
  }

  // Overlay for readability when background image is present
  const hasOverlay = Boolean(backgroundImage);

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        ...bgSx,
        overflow: 'hidden',
      }}
    >
      {hasOverlay && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.35)',
            zIndex: 0,
          }}
        />
      )}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems,
            textAlign,
            maxWidth: alignment === 'center' ? 720 : 680,
            mx: alignment === 'center' ? 'auto' : undefined,
          }}
        >
          {eyebrow && (
            <RevealOnScroll delay={0}>
              <Typography
                variant="overline"
                sx={{
                  color: hasOverlay
                    ? 'rgba(255,255,255,0.85)'
                    : theme.palette.primary.main,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                {eyebrow}
              </Typography>
            </RevealOnScroll>
          )}

          {heading && (
            <RevealOnScroll delay={0.08}>
              <Typography
                component={motion.h1}
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.25rem' },
                  lineHeight: 1.15,
                  color: hasOverlay ? '#fff' : 'text.primary',
                  letterSpacing: '-0.02em',
                  mb: subheading ? 1.5 : body || safeButtons.length ? 2.5 : 0,
                }}
              >
                {heading}
              </Typography>
            </RevealOnScroll>
          )}

          {subheading && (
            <RevealOnScroll delay={0.13}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                  color: hasOverlay ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                  lineHeight: 1.4,
                  mb: body || safeButtons.length ? 2.5 : 0,
                }}
              >
                {subheading}
              </Typography>
            </RevealOnScroll>
          )}

          {body && (
            <RevealOnScroll delay={0.18}>
              <Typography
                variant="body1"
                sx={{
                  color: hasOverlay ? 'rgba(255,255,255,0.78)' : 'text.secondary',
                  lineHeight: 1.75,
                  maxWidth: 600,
                  mb: safeButtons.length ? 3.5 : 0,
                  whiteSpace: 'pre-line',
                }}
              >
                {body}
              </Typography>
            </RevealOnScroll>
          )}

          {safeButtons.length > 0 && (
            <RevealOnScroll delay={0.24}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: alignItems,
                  mt: !heading && !subheading && !body ? 0 : undefined,
                }}
              >
                {safeButtons.map((btn, idx) => (
                  btn.label && btn.href ? (
                    <Button
                      key={btn.id || idx}
                      variant={idx === 0 ? 'contained' : 'outlined'}
                      size="large"
                      href={btn.href}
                      sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '1rem',
                        minHeight: 48,
                        ...(idx === 0 && hasOverlay
                          ? {
                              bgcolor: '#fff',
                              color: 'text.primary',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }
                          : {}),
                        ...(idx > 0 && hasOverlay
                          ? {
                              borderColor: 'rgba(255,255,255,0.6)',
                              color: '#fff',
                              '&:hover': {
                                borderColor: '#fff',
                                bgcolor: 'rgba(255,255,255,0.08)',
                              },
                            }
                          : {}),
                      }}
                    >
                      {btn.label}
                    </Button>
                  ) : null
                ))}
              </Box>
            </RevealOnScroll>
          )}
        </Box>
      </Container>
    </Box>
  );
}

GenericHeroSection.propTypes = {
  eyebrow: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  body: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      href: PropTypes.string,
    })
  ),
};

GenericHeroSection.defaultProps = {
  alignment: 'left',
  buttons: [],
};
